import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AdminHeader from '../../components/ui/AdminHeader';
import AdminClientSelector from '../../components/ui/AdminClientSelector';
import ItemDetailsForm from './components/ItemDetailsForm';
import ImageGalleryManager from './components/ImageGalleryManager';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';

import { updateInventoryItem, getInventoryPhotos, uploadInventoryPhoto, addInventoryPhotoRecord, deleteInventoryPhoto, setPrimaryPhoto, getInventoryItem } from '../../services/laseaInventoryService';

const EditItemInformation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { itemId: urlItemId } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [itemId, setItemId] = useState(null);
  const [existingPhotos, setExistingPhotos] = useState([]);

  const [formData, setFormData] = useState({
    liin: '',
    name: '',
    status: 'great',
    deliveryStatus: 'in_warehouse',
    arrivalDate: '',
    itemValue: '',
    notes: '',
    location: 'Portland, OR'
  });

  const [images, setImages] = useState([]);
  const [newImages, setNewImages] = useState([]); // Track new images to upload

  const [primaryImageIndex, setPrimaryImageIndex] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  // Load item data on mount
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const loadItemData = async () => {
      // Safety-net: if the load gets stuck for more than 30s, clear the spinner
      const safetyTimer = setTimeout(() => {
        if (!signal.aborted) {
          console.warn('loadItemData safety timeout fired – clearing loading state');
          setLoading(false);
        }
      }, 30000);

      try {
        if (!signal.aborted) setLoading(true);
        setError('');

        // Determine item ID: either from URL parameter (QR scan) or localStorage
        let inventoryId;
        let editingItem;

        if (urlItemId) {
          // QR code scan - use the item ID directly
          inventoryId = urlItemId;
          editingItem = { id: inventoryId };
        } else {
          // Regular navigation - get from localStorage
          const editingItemStr = localStorage.getItem('editingItem');
          if (!editingItemStr || editingItemStr === 'undefined') {
            setError('No item selected for editing');
            return;
          }
          try {
            editingItem = JSON.parse(editingItemStr);
          } catch (e) {
            console.error('Failed to parse editingItem from localStorage', e);
            setError('Invalid item configuration preserved');
            return;
          }
          inventoryId = editingItem?.id;
        }

        if (!inventoryId) {
          setError('Invalid item data');
          return;
        }

        if (signal.aborted) return;
        setItemId(inventoryId);

        // Fetch fresh data from Supabase
        const itemData = await getInventoryItem(inventoryId);
        if (signal.aborted) return;

        // Format the data for the form
        const loadedData = {
          liin: editingItem?.liin || `LIIN-${String(inventoryId)?.slice(0, 8)?.toUpperCase()}`,
          name: itemData?.itemName || '',
          status: itemData?.condition || 'great',
          deliveryStatus: itemData?.deliveryStatus || 'in_warehouse',
          arrivalDate: itemData?.createdAt ? new Date(itemData?.createdAt)?.toISOString()?.split('T')?.[0] : '',
          itemValue: itemData?.currentValue || '',
          notes: itemData?.notes || '',
          location: itemData?.location || 'Portland, OR'
        };

        setFormData(loadedData);
        setInitialData(loadedData);

        console.log('📂 Loaded item from database - currentValue:', itemData?.currentValue, 'type:', typeof itemData?.currentValue);

        // Fetch photos from Supabase
        const photos = await getInventoryPhotos(inventoryId);
        if (signal.aborted) return;

        setExistingPhotos(photos);

        // Convert photos to images format for display
        if (photos?.length > 0) {
          const imageUrls = photos?.map(photo => ({
            url: photo?.signedUrl,
            alt: itemData?.itemName || 'Inventory item photo',
            photoId: photo?.id
          }));
          setImages(imageUrls);

          // Set primary image index
          const primaryIndex = photos?.findIndex(photo => photo?.isPrimary);
          if (primaryIndex !== -1) {
            setPrimaryImageIndex(primaryIndex);
          }
        }
      } catch (err) {
        if (signal.aborted) return; // Ignore errors from cancelled loads
        console.error('Error loading item data:', err);
        setError(err?.message || 'Failed to load item data');
      } finally {
        clearTimeout(safetyTimer);
        if (!signal.aborted) setLoading(false);
      }
    };

    loadItemData();

    return () => {
      // Cancel in-flight fetches if this component unmounts
      controller.abort();
    };
  }, [urlItemId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Furniture name is required';
    }

    if (!formData?.status) {
      newErrors.status = 'Condition status is required';
    }

    if (!formData?.arrivalDate) {
      newErrors.arrivalDate = 'Arrival date is required';
    }

    // Note: notes field has no length limit since it stores structured
    // auto-logged update history entries that can grow over time

    if (existingPhotos?.length === 0 && newImages?.length === 0) {
      newErrors.images = 'At least one image is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      alert('Please fix the missing required fields before saving.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    try {
      setIsSubmitting(true);

      // 1. Update inventory item in Supabase
      const updates = {
        itemName: formData?.name,
        description: formData?.description,
        category: formData?.category,
        condition: formData?.status,
        deliveryStatus: formData?.deliveryStatus,
        purchasePrice: formData?.purchasePrice ? parseFloat(formData?.purchasePrice) : null,
        currentValue: formData?.itemValue ? parseFloat(formData?.itemValue) : null,
        location: formData?.location,
        notes: formData?.notes
      };

      await updateInventoryItem(itemId, updates);

      // 2. Upload new photos if any (only images with file objects)
      if (newImages?.length > 0) {
        for (let i = 0; i < newImages?.length; i++) {
          const image = newImages?.[i];

          // Only upload if this is a new image with a file object
          if (image?.file) {
            // Upload photo to storage
            const photoPath = await uploadInventoryPhoto(image?.file, itemId);

            // Add photo record to database
            await addInventoryPhotoRecord(
              itemId,
              photoPath,
              existingPhotos?.length === 0 && i === 0, // First image is primary if no existing photos
              existingPhotos?.length + i // Display order continues from existing
            );
          }
        }
      }

      alert('Item updated successfully!');
      navigate('/client-inventory-management');
    } catch (error) {
      console.error('Error updating item:', error);
      alert(`Failed to update item: ${error?.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePhoto = async (photoId, photoPath) => {
    try {
      await deleteInventoryPhoto(photoId, photoPath);

      // Reload photos
      const photos = await getInventoryPhotos(itemId);
      setExistingPhotos(photos);
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const handleSetPrimaryPhoto = async (photoId) => {
    try {
      await setPrimaryPhoto(itemId, photoId);

      // Reload photos
      const photos = await getInventoryPhotos(itemId);
      setExistingPhotos(photos);
    } catch (error) {
      console.error('Error setting primary photo:', error);
      alert('Failed to set primary photo. Please try again.');
    }
  };

  // Human-readable labels for delivery status values
  const deliveryStatusLabel = (val) => {
    switch (val?.toLowerCase()) {
      case 'in_warehouse': return 'In Warehouse';
      case 'in_route': return 'In Route';
      case 'delivered': return 'Delivered';
      default: return val || 'Unknown';
    }
  };

  const handleSave = async () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (!validateForm()) {
      alert('Please fill in all required fields correctly.');
      return;
    }

    try {
      setIsSaving(true);

      // Build update history log entries for any changed fields
      let updatedNotes = formData?.notes || '';
      // Use full ISO datetime so the client modal can show "Feb 19, 2026, 3:45 PM"
      const nowISO = new Date().toISOString();

      const newLogs = [];

      // Check for condition change
      if (initialData && formData.status !== initialData.status) {
        newLogs.push(`[${nowISO}] Condition updated: ${initialData.status} -> ${formData.status}`);
      }

      // Check for delivery status change
      if (initialData && formData.deliveryStatus !== initialData.deliveryStatus) {
        newLogs.push(`[${nowISO}] Status updated: ${deliveryStatusLabel(initialData.deliveryStatus)} -> ${deliveryStatusLabel(formData.deliveryStatus)}`);
      }

      // Check for location change
      if (initialData && formData.location !== initialData.location) {
        newLogs.push(`[${nowISO}] Location updated: ${initialData.location} -> ${formData.location}`);
      }

      // Append new logs to notes if any
      if (newLogs.length > 0) {
        const logString = newLogs.join('\n');
        // Add newline if notes already exist
        updatedNotes = updatedNotes ? `${updatedNotes}\n\n${logString}` : logString;
      }

      const updates = {
        itemName: formData?.name,
        condition: formData?.status,
        deliveryStatus: formData?.deliveryStatus,
        currentValue: formData?.itemValue ? parseFloat(formData?.itemValue) : null,
        notes: updatedNotes,
        location: formData?.location
      };

      console.log('💾 Saving item - formData.itemValue:', formData?.itemValue, 'type:', typeof formData?.itemValue);
      console.log('💾 Saving item - parsed currentValue:', formData?.itemValue ? parseFloat(formData?.itemValue) : null);
      const timeout = (ms, msg) => new Promise((_, reject) => setTimeout(() => reject(new Error(msg)), ms));

      console.log('💾 Saving item - full updates object:', JSON.stringify(updates, null, 2));

      const result = await Promise.race([
        updateInventoryItem(itemId, updates),
        timeout(15000, 'Database update timed out. Please check your connection.')
      ]);

      console.log('✅ Save successful - returned data:', result);

      // Upload new photos if any (only images with file objects)
      let uploadedPhotoCount = 0;
      if (newImages?.length > 0) {
        console.log('📤 Uploading', newImages.length, 'new photos...');
        for (let i = 0; i < newImages?.length; i++) {
          const image = newImages?.[i];

          if (image?.file) {
            const progressMsg = `Uploading photo ${i + 1} of ${newImages.length}...`;
            console.log('📤', progressMsg);
            setUploadProgress(progressMsg);

            // Upload photo to storage
            const uploadReq = uploadInventoryPhoto(image?.file, itemId);
            const photoPath = await Promise.race([
              uploadReq,
              timeout(30000, 'Photo upload timed out. Please check your connection.')
            ]);

            // Add photo record to database
            await addInventoryPhotoRecord(
              itemId,
              photoPath,
              existingPhotos?.length === 0 && i === 0,
              existingPhotos?.length + i
            );
            uploadedPhotoCount++;
          }
        }

        // Log the photo addition to the item's update history so clients can see it
        if (uploadedPhotoCount > 0) {
          const photoLog = `[${nowISO}] Photos added: ${uploadedPhotoCount} new photo${uploadedPhotoCount !== 1 ? 's' : ''} uploaded`;
          const notesWithPhotoLog = updatedNotes
            ? `${updatedNotes}\n\n${photoLog}`
            : photoLog;
          // Update notes again with the photo log appended
          await updateInventoryItem(itemId, { notes: notesWithPhotoLog });
        }
      }

      setShowSuccessMessage(true);

      setTimeout(() => {
        setShowSuccessMessage(false);
        navigate('/client-inventory-management', {
          state: { message: 'Item updated successfully' }
        });
      }, 2000);
    } catch (error) {
      console.error('Error updating item:', error);
      alert(`Failed to update item: ${error?.message}`);
    } finally {
      setIsSaving(false);
      setUploadProgress(null);
    }
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    let existingInventory = [];
    try {
      const storedInv = localStorage.getItem('clientInventory');
      if (storedInv && storedInv !== 'undefined') {
        existingInventory = JSON.parse(storedInv);
      }
    } catch (e) { }
    const updatedInventory = existingInventory?.filter((item) => item?.liin !== formData?.liin);
    localStorage.setItem('clientInventory', JSON.stringify(updatedInventory));

    setIsDeleteModalOpen(false);
    navigate('/client-inventory-management', {
      state: { message: 'Item deleted successfully' }
    });
  };

  const handleCancel = () => {
    navigate('/client-inventory-management');
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <div className="flex pt-16">
        <AdminClientSelector
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onClientSelect={() => { }} />


        <main className="flex-1 p-4 lg:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-4">

                <Icon name="ArrowLeft" size={16} />
                <span>Back to Inventory</span>
              </button>

              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-semibold text-foreground mb-2">
                    Edit Item Information
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Update furniture details and manage images
                  </p>
                </div>

                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-md hover:bg-muted transition-smooth touch-target"
                  aria-label="Open client selector">

                  <Icon name="Menu" size={24} />
                </button>
              </div>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading item data...</p>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-center gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <div>
                  <p className="text-sm font-medium text-error">{error}</p>
                  <button
                    onClick={() => navigate('/client-inventory-management')}
                    className="text-xs text-error underline mt-1"
                  >
                    Return to Inventory
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && (
              <>
                {showSuccessMessage &&
                  <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3">
                    <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
                    <p className="text-sm font-medium text-success">
                      Changes saved successfully!
                    </p>
                  </div>
                }

                <div className="bg-card rounded-lg border border-border elevation-1 p-6 lg:p-8 mb-6">
                  <ItemDetailsForm
                    formData={formData}
                    errors={errors}
                    onChange={setFormData} />

                </div>

                <div className="bg-card rounded-lg border border-border elevation-1 p-6 lg:p-8 mb-6">
                  <ImageGalleryManager
                    images={newImages}
                    onImagesChange={setNewImages}
                    existingPhotos={existingPhotos}
                    onDeleteExisting={handleDeletePhoto}
                    onSetExistingPrimary={handleSetPrimaryPhoto}
                    primaryImageIndex={primaryImageIndex}
                    onPrimaryImageChange={setPrimaryImageIndex} />

                  {errors?.images &&
                    <p className="text-xs text-error mt-2">{errors?.images}</p>
                  }
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleCancel}
                    iconName="X"
                    iconPosition="left"
                    iconSize={16}>

                    Cancel
                  </Button>

                  <Button
                    variant="destructive"
                    fullWidth
                    onClick={handleDelete}
                    iconName="Trash2"
                    iconPosition="left"
                    iconSize={16}>

                    Delete Item
                  </Button>

                  <Button
                    variant="default"
                    fullWidth
                    onClick={handleSave}
                    loading={isSaving}
                    iconName="Save"
                    iconPosition="left"
                    iconSize={16}>

                    {isSaving ? (uploadProgress || 'Saving...') : 'Save Changes'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemName={formData?.name} />

    </div>);

};

export default EditItemInformation;